/** Lint rules and draft-diffing for dashboard template filter editing. */

export interface LintIssue {
  key: string;
  message: string;
}

/**
 * Catch broken template configurations before they are saved:
 * - CLUVIO_FILTER / TIME_RANGE without a cluvioFilterID link
 * - TIME_RANGE / DROPDOWN without any options to choose from
 * - duplicate keys across the fixed + variable lists
 */
export function lintTemplateFilters(
  fixedFilters: Record<string, any>[],
  variableFilters: Record<string, any>[]
): LintIssue[] {
  const issues: LintIssue[] = [];

  const seen = new Map<string, string>();
  for (const f of [...fixedFilters, ...variableFilters]) {
    if (f.key && seen.has(f.key)) {
      issues.push({ key: f.key, message: `Duplicate key "${f.key}" (also used in ${seen.get(f.key)})` });
    }
    if (f.key) seen.set(f.key, 'another list');
  }

  for (const f of variableFilters) {
    if (f.fieldType === 'CLUVIO_FILTER' && !f.cluvioFilterID) {
      issues.push({ key: f.key, message: `"${f.label}" is not linked to a Cluvio filter` });
    }
    if (f.fieldType === 'TIME_RANGE' && (!Array.isArray(f.fieldOptions) || f.fieldOptions.filter(Boolean).length === 0)) {
      issues.push({ key: f.key, message: `"${f.label}" has no time range options` });
    }
    if (f.fieldType === 'DROPDOWN' && (!Array.isArray(f.fieldOptions) || f.fieldOptions.filter(Boolean).length === 0)) {
      issues.push({ key: f.key, message: `"${f.label}" dropdown has no options` });
    }
  }

  for (const f of fixedFilters) {
    if (f.fixedType === 'FILTER' && !f.cluvioFilterID) {
      issues.push({ key: f.key, message: `"${f.label}" is not linked to a Cluvio filter` });
    }
  }

  return issues;
}

export interface FilterDiff {
  added: string[];
  removed: string[];
  changed: string[];
}

/** Compare draft filter arrays against the stored originals, keyed by `key`. */
export function diffFilters(
  original: Record<string, any>[],
  draft: Record<string, any>[]
): FilterDiff {
  const originalByKey = new Map(original.map((f) => [f.key, f]));
  const draftByKey = new Map(draft.map((f) => [f.key, f]));

  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];

  for (const [key, d] of draftByKey) {
    const o = originalByKey.get(key);
    if (!o) added.push(key);
    else if (JSON.stringify(o) !== JSON.stringify(d)) changed.push(key);
  }
  for (const key of originalByKey.keys()) {
    if (!draftByKey.has(key)) removed.push(key);
  }

  return { added, removed, changed };
}

export function diffIsEmpty(diff: FilterDiff): boolean {
  return diff.added.length === 0 && diff.removed.length === 0 && diff.changed.length === 0;
}
